// HerbalSoapProducts.js - Herbal soap themed Products section component
import { DEVICES } from '../../../constants/breakpoints';

/**
 * Creates a herbal soap themed Products section with product cards
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the products section is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The products elements
 */
export const createHerbalSoapProductsElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const productsSectionClass = 'herbal-soap-products-section';
  const productsContainerClass = 'herbal-soap-products-container';
  const productsHeaderClass = 'herbal-soap-products-header';
  const productsTitleClass = 'herbal-soap-products-title';
  const productsSubtitleClass = 'herbal-soap-products-subtitle';
  const productsGridClass = 'herbal-soap-products-grid';
  const productCardClass = 'herbal-soap-product-card';
  const productImageWrapperClass = 'herbal-soap-product-image-wrapper';
  const productImageClass = 'herbal-soap-product-image';
  const productContentClass = 'herbal-soap-product-content';
  const productNameClass = 'herbal-soap-product-name';
  const productDescriptionClass = 'herbal-soap-product-description';
  const productPriceClass = 'herbal-soap-product-price';
  const productButtonClass = 'herbal-soap-product-button';
  const productBadgeClass = 'herbal-soap-product-badge';

  // Desktop Styles - Herbal theme with natural green colors
  const productsSectionStyles = {
    padding: '100px 20px',
    backgroundColor: '#f8fffe',
    background: `
      linear-gradient(135deg, #f8fffe 0%, #f0fdf4 50%, #ecfdf5 100%),
      radial-gradient(circle at 20% 80%, rgba(144, 238, 144, 0.05) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(34, 139, 34, 0.03) 0%, transparent 50%)
    `,
    position: 'relative',
    overflow: 'hidden'
  };

  const productsContainerStyles = {
    maxWidth: '1400px',
    margin: '0 auto',
    width: '100%'
  };

  const productsHeaderStyles = {
    textAlign: 'center',
    marginBottom: '80px'
  };

  const productsTitleStyles = {
    fontSize: '48px',
    fontWeight: '700',
    color: '#1a3d1a',
    marginBottom: '20px',
    fontFamily: '"Inter", sans-serif',
    lineHeight: '1.2'
  };

  const productsSubtitleStyles = {
    fontSize: '20px',
    color: '#4a5568',
    maxWidth: '600px',
    margin: '0 auto',
    lineHeight: '1.6',
    fontFamily: '"Inter", sans-serif'
  };

  const productsGridStyles = {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '40px',
    alignItems: 'start'
  };

  const productCardStyles = {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    transition: 'all 0.4s ease',
    border: '1px solid rgba(144, 238, 144, 0.2)',
    position: 'relative'
  };

  const productCardHoverStyles = {
    transform: 'translateY(-8px)',
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(144, 238, 144, 0.3)',
    borderColor: 'rgba(144, 238, 144, 0.4)'
  };

  const productImageWrapperStyles = {
    position: 'relative',
    width: '100%',
    height: '280px',
    overflow: 'hidden'
  };

  const productImageStyles = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'all 0.4s ease'
  };

  const productImageHoverStyles = {
    transform: 'scale(1.05)'
  };

  const productBadgeStyles = {
    position: 'absolute',
    top: '16px',
    left: '16px',
    backgroundColor: '#90EE90',
    color: '#1a3d1a',
    padding: '6px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  };

  const productContentStyles = {
    padding: '32px 24px'
  };

  const productNameStyles = {
    fontSize: '24px',
    fontWeight: '600',
    color: '#1a3d1a',
    marginBottom: '12px',
    fontFamily: '"Inter", sans-serif'
  };

  const productDescriptionStyles = {
    fontSize: '14px',
    color: '#6b7280',
    lineHeight: '1.6',
    marginBottom: '20px',
    fontFamily: '"Inter", sans-serif'
  };

  const productPriceStyles = {
    fontSize: '28px',
    fontWeight: '700',
    color: '#228b22',
    marginBottom: '24px',
    fontFamily: '"Inter", sans-serif'
  };

  const productButtonStyles = {
    width: '100%',
    padding: '14px 24px',
    backgroundColor: '#228b22',
    background: `linear-gradient(135deg, #228b22 0%, #90EE90 100%)`,
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    fontFamily: '"Inter", sans-serif'
  };

  const productButtonHoverStyles = {
    background: `linear-gradient(135deg, #1a3d1a 0%, #228b22 100%)`,
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(34, 139, 34, 0.3)'
  };

  // Tablet Styles
  const tabletProductsGridStyles = {
    ...productsGridStyles,
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '30px'
  };

  const tabletProductsTitleStyles = {
    ...productsTitleStyles,
    fontSize: '40px'
  };

  const tabletProductsSubtitleStyles = {
    ...productsSubtitleStyles,
    fontSize: '18px'
  };

  // Mobile Styles
  const mobileProductsSectionStyles = {
    ...productsSectionStyles,
    padding: '60px 20px'
  };

  const mobileProductsHeaderStyles = {
    ...productsHeaderStyles,
    marginBottom: '50px'
  };

  const mobileProductsTitleStyles = {
    ...productsTitleStyles,
    fontSize: '32px'
  };

  const mobileProductsSubtitleStyles = {
    ...productsSubtitleStyles,
    fontSize: '16px'
  };

  const mobileProductsGridStyles = {
    ...productsGridStyles,
    gridTemplateColumns: '1fr',
    gap: '24px'
  };

  const mobileProductImageWrapperStyles = {
    ...productImageWrapperStyles,
    height: '240px'
  };

  const mobileProductContentStyles = {
    ...productContentStyles,
    padding: '24px 20px'
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(productsSectionClass, productsSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(productsContainerClass, productsContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(productsHeaderClass, productsHeaderStyles);
    newRegistry[DEVICES.DESKTOP].set(productsTitleClass, productsTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(productsSubtitleClass, productsSubtitleStyles);
    newRegistry[DEVICES.DESKTOP].set(productsGridClass, productsGridStyles);
    newRegistry[DEVICES.DESKTOP].set(productCardClass, productCardStyles);
    newRegistry[DEVICES.DESKTOP].set(productImageWrapperClass, productImageWrapperStyles);
    newRegistry[DEVICES.DESKTOP].set(productImageClass, productImageStyles);
    newRegistry[DEVICES.DESKTOP].set(productBadgeClass, productBadgeStyles);
    newRegistry[DEVICES.DESKTOP].set(productContentClass, productContentStyles);
    newRegistry[DEVICES.DESKTOP].set(productNameClass, productNameStyles);
    newRegistry[DEVICES.DESKTOP].set(productDescriptionClass, productDescriptionStyles);
    newRegistry[DEVICES.DESKTOP].set(productPriceClass, productPriceStyles);
    newRegistry[DEVICES.DESKTOP].set(productButtonClass, productButtonStyles);

    // Hover styles
    newRegistry[DEVICES.DESKTOP].set(`${productCardClass}--hover`, productCardHoverStyles);
    newRegistry[DEVICES.DESKTOP].set(`${productImageClass}--hover`, productImageHoverStyles);
    newRegistry[DEVICES.DESKTOP].set(`${productButtonClass}--hover`, productButtonHoverStyles);

    // Tablet styles
    newRegistry[DEVICES.TABLET].set(productsGridClass, tabletProductsGridStyles);
    newRegistry[DEVICES.TABLET].set(productsTitleClass, tabletProductsTitleStyles);
    newRegistry[DEVICES.TABLET].set(productsSubtitleClass, tabletProductsSubtitleStyles);

    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(productsSectionClass, mobileProductsSectionStyles);
    newRegistry[DEVICES.MOBILE].set(productsHeaderClass, mobileProductsHeaderStyles);
    newRegistry[DEVICES.MOBILE].set(productsTitleClass, mobileProductsTitleStyles);
    newRegistry[DEVICES.MOBILE].set(productsSubtitleClass, mobileProductsSubtitleStyles);
    newRegistry[DEVICES.MOBILE].set(productsGridClass, mobileProductsGridStyles);
    newRegistry[DEVICES.MOBILE].set(productImageWrapperClass, mobileProductImageWrapperStyles);
    newRegistry[DEVICES.MOBILE].set(productContentClass, mobileProductContentStyles);

    return newRegistry;
  });

  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    productsSectionClass,
    productsContainerClass,
    productsHeaderClass,
    productsTitleClass,
    productsSubtitleClass,
    productsGridClass,
    productCardClass,
    productImageWrapperClass,
    productImageClass,
    productBadgeClass,
    productContentClass,
    productNameClass,
    productDescriptionClass,
    productPriceClass,
    productButtonClass
  ]));

  // Generate IDs for all elements
  const productsSectionId = Date.now();
  const productsContainerId = productsSectionId + 1;
  const productsHeaderId = productsSectionId + 2;
  const productsTitleId = productsSectionId + 3;
  const productsSubtitleId = productsSectionId + 4;
  const productsGridId = productsSectionId + 5;

  // Product data
  const products = [
    {
      name: 'Herbal Soap',
      description: 'Natural handmade soap with organic herbs and essential oils for gentle cleansing.',
      price: '$12.99',
      image: 'https://images.unsplash.com/photo-1556228720-195a672e8a03?q=80&w=2187&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
      badge: 'Bestseller',
      linkTo: 'herbal-soap'
    },
    {
      name: 'Ubtan',
      description: 'Traditional herbal face pack with turmeric, gram flour, and natural ingredients.',
      price: '$8.99',
      image: 'https://images.unsplash.com/photo-1596755389378-c31d21fd1273?q=80&w=2187&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
      badge: 'Natural',
      linkTo: 'herbal-ubtan'
    },
    {
      name: 'Herbal Facewash',
      description: 'Gentle herbal face cleanser with neem, tulsi, and aloe vera for healthy skin.',
      price: '$15.99',
      image: 'https://images.unsplash.com/photo-1571781926291-c477ebfd024b?q=80&w=2187&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
      badge: 'Organic',
      linkTo: 'herbal-facewash'
    }
  ];

  // Create product card elements
  let currentId = productsGridId + 1;
  const productElements = [];

  products.forEach((product, index) => {
    const cardId = currentId++;
    const imageWrapperId = currentId++;
    const imageId = currentId++;
    const badgeId = currentId++;
    const contentId = currentId++;
    const nameId = currentId++;
    const descriptionId = currentId++;
    const priceId = currentId++;
    const buttonId = currentId++;

    // Product Card
    productElements.push({
      id: cardId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: `${product.name} Card`,
      parentId: productsGridId,
      hierarchyName: `${product.name} Card`,
      className: productCardClass,
      styles: {},
      children: [imageWrapperId, contentId],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-product-card'
      }
    });

    // Image Wrapper
    productElements.push({
      id: imageWrapperId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: `${product.name} Image Wrapper`,
      parentId: cardId,
      hierarchyName: `${product.name} Image Wrapper`,
      className: productImageWrapperClass,
      styles: {},
      children: [imageId, badgeId],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-product-image-wrapper'
      }
    });

    // Product Image
    productElements.push({
      id: imageId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: `${product.name} Image`,
      parentId: imageWrapperId,
      hierarchyName: `${product.name} Image`,
      className: productImageClass,
      styles: {},
      children: [],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      src: product.image,
      attributes: {
        'data-element': 'herbal-soap-product-image',
        'alt': product.name
      }
    });

    // Product Badge
    productElements.push({
      id: badgeId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: `${product.name} Badge`,
      parentId: imageWrapperId,
      hierarchyName: `${product.name} Badge`,
      className: productBadgeClass,
      styles: {},
      children: [],
      text: product.badge,
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-product-badge'
      }
    });

    // Product Content
    productElements.push({
      id: contentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: `${product.name} Content`,
      parentId: cardId,
      hierarchyName: `${product.name} Content`,
      className: productContentClass,
      styles: {},
      children: [nameId, descriptionId, priceId, buttonId],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-product-content'
      }
    });

    // Product Name
    productElements.push({
      id: nameId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: `${product.name} Name`,
      parentId: contentId,
      hierarchyName: `${product.name} Name`,
      className: productNameClass,
      styles: {},
      children: [],
      text: product.name,
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-product-name'
      }
    });

    // Product Description
    productElements.push({
      id: descriptionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: `${product.name} Description`,
      parentId: contentId,
      hierarchyName: `${product.name} Description`,
      className: productDescriptionClass,
      styles: {},
      children: [],
      text: product.description,
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-product-description'
      }
    });

    // Product Price
    productElements.push({
      id: priceId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: `${product.name} Price`,
      parentId: contentId,
      hierarchyName: `${product.name} Price`,
      className: productPriceClass,
      styles: {},
      children: [],
      text: product.price,
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-product-price'
      }
    });

    // Order Button
    productElements.push({
      id: buttonId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'button',
      name: `${product.name} Order Button`,
      parentId: contentId,
      hierarchyName: `${product.name} Order Button`,
      className: productButtonClass,
      styles: {},
      children: [],
      text: 'Order Now',
      visibility: 'visible',
      isExpanded: true,
      linkTo: product.linkTo,
      attributes: {
        'data-element': 'herbal-soap-product-button'
      }
    });
  });

  // Get product card IDs for the grid
  const productCardIds = productElements
    .filter(element => element.parentId === productsGridId)
    .map(element => element.id);

  // Create base elements
  const baseElements = [
    // Main Products Section
    {
      id: productsSectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Herbal Soap Products Section',
      parentId: null,
      hierarchyName: 'Herbal Soap Products Section',
      className: productsSectionClass,
      styles: {},
      children: [productsContainerId],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-products-section',
        'data-component': 'section',
        'data-section-type': 'products',
        'data-page-id': currentPageId
      }
    },

    // Container
    {
      id: productsContainerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Products Container',
      parentId: productsSectionId,
      hierarchyName: 'Products Container',
      className: productsContainerClass,
      styles: {},
      children: [productsHeaderId, productsGridId],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-products-container'
      }
    },

    // Header
    {
      id: productsHeaderId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Products Header',
      parentId: productsContainerId,
      hierarchyName: 'Products Header',
      className: productsHeaderClass,
      styles: {},
      children: [productsTitleId, productsSubtitleId],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-products-header'
      }
    },

    // Title
    {
      id: productsTitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h2',
      name: 'Products Title',
      parentId: productsHeaderId,
      hierarchyName: 'Products Title',
      className: productsTitleClass,
      styles: {},
      children: [],
      text: 'Our Natural Products',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-products-title'
      }
    },

    // Subtitle
    {
      id: productsSubtitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Products Subtitle',
      parentId: productsHeaderId,
      hierarchyName: 'Products Subtitle',
      className: productsSubtitleClass,
      styles: {},
      children: [],
      text: 'Discover our premium collection of handcrafted herbal products made with 100% natural ingredients for your skin and wellness.',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-products-subtitle'
      }
    },

    // Products Grid
    {
      id: productsGridId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Products Grid',
      parentId: productsContainerId,
      hierarchyName: 'Products Grid',
      className: productsGridClass,
      styles: {},
      children: productCardIds,
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-products-grid'
      }
    }
  ];

  // Combine all elements
  const allElements = [...baseElements, ...productElements];
  
  // Return the created elements
  return {
    elements: allElements
  };
}; 